import { signal } from '@preact/signals-core';
import { html, nothing, render as renderLit, type TemplateResult } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ref } from 'lit/directives/ref.js';

import {
  buildEmbeddingBadgeViewModel,
  embeddingBadgeStyles,
  type EmbeddingBadgeViewModel,
  renderEmbeddingBadge,
} from './embedding-badge';
import {
  type EmbeddingIndexReviewCurrentValue,
  type EmbeddingIndexReviewField,
  type EmbeddingIndexReviewLocator,
  type EmbeddingIndexReviewRegenerableField,
  type EmbeddingIndexReviewSubjectSummary,
  EmbeddingStatusProvider,
  getEmbeddingStatusLabel,
  type IEmbeddingStatusService,
  isEmbeddingCreditsRequiredError,
  isEmbeddingStatusUnavailableError,
} from './embedding-status-service';

export interface EmbeddingBadgeControllerHost {
  model: { id: string };
  requestUpdate(): void;
  std: {
    getOptional<T>(identifier: unknown): T | undefined;
  };
  store: { id: string };
}

export interface EmbeddingBadgeControllerOptions {
  canRender?: () => boolean;
  canTrack?: () => boolean;
  getIndexReviewLocator?: () => EmbeddingIndexReviewLocator | null;
  getPortalAnchor?: () => HTMLElement | null;
  idlePollMs?: number | null;
  includeWorkspaceProgress?: boolean;
  mediaType?: () => string | null;
  notify?: (message: string) => void;
}

type DisposableLike =
  | (() => void)
  | { dispose?: () => void; unsubscribe?: () => void }
  | null
  | undefined;

type ViewportAwareHost = HTMLElement & {
  gfx?: {
    viewport?: {
      zoom?: number;
      viewportUpdated?: {
        subscribe?: (listener: () => void) => DisposableLike;
      };
    };
  };
};

let portalStylesInjected = false;
const PORTAL_CLOSE_DELAY_MS = 420;

function ensureEmbeddingBadgePortalStyles() {
  if (portalStylesInjected || typeof document === 'undefined') return;
  portalStylesInjected = true;
  const style = document.createElement('style');
  style.setAttribute('data-embedding-badge-portal-styles', '');
  style.textContent = embeddingBadgeStyles.cssText;
  document.head.append(style);
}

/**
 * Shared embedding badge state controller for media blocks.
 *
 * Image and attachment blocks differ only in visibility/eligibility rules and
 * whether they want workspace batch progress. The provider subscription,
 * block-level indexing check, re-index action, polling fallback and toast
 * lifecycle stay centralized here.
 */
export class EmbeddingBadgeController {
  private readonly indexed$ = signal<boolean | null>(null);
  private readonly loading$ = signal(false);
  private readonly failed$ = signal(false);
  private readonly expanded$ = signal(false);
  private readonly enabled$ = signal<boolean | null>(null);
  private readonly unavailable$ = signal(false);
  private readonly reindexCreditsBlocked$ = signal(false);
  private readonly hoverIntent$ = signal(false);
  private readonly portalActive$ = signal(false);
  private readonly reviewOpen$ = signal(false);
  private readonly reviewLoading$ = signal(false);
  private readonly reviewError$ = signal<string | null>(null);
  private readonly reviewNotice$ = signal<string | null>(null);
  private readonly reviewBusyField$ = signal<string | null>(null);
  private readonly reviewSavedField$ = signal<EmbeddingIndexReviewField | null>(
    null
  );
  private readonly reviewRegenerationOpenField$ =
    signal<EmbeddingIndexReviewRegenerableField | null>(null);
  private readonly reviewSummary$ =
    signal<EmbeddingIndexReviewSubjectSummary | null>(null);
  private readonly batchProgress$ = signal<{
    indexed: number;
    total: number;
  } | null>(null);
  private readonly docProgress$ = signal<{
    indexed: number;
    total: number;
  } | null>(null);

  private checkTimer?: ReturnType<typeof setInterval>;
  private unsubscribeDoc?: () => void;
  private unsubscribeEnabled?: () => void;
  private unsubscribeProgress?: () => void;
  private userRequestedIndex = false;
  private reviewSummaryLoading = false;
  private reviewSummaryLocatorKey: string | null = null;
  private readonly reviewDrafts = new Map<EmbeddingIndexReviewField, string>();
  private readonly reviewHints = new Map<
    EmbeddingIndexReviewRegenerableField,
    string
  >();
  private portalElement: HTMLDivElement | null = null;
  private portalPositionRaf: number | null = null;
  private portalResizeObserver: ResizeObserver | null = null;
  private portalViewportSubscription: DisposableLike = null;
  private portalTrackingActive = false;
  private portalUsesCanvasZoom = true;
  private portalCloseTimer?: ReturnType<typeof setTimeout>;
  private reviewSavedTimer?: ReturnType<typeof setTimeout>;
  private pointerInsideBadge = false;

  constructor(
    private readonly host: EmbeddingBadgeControllerHost,
    private readonly options: EmbeddingBadgeControllerOptions = {}
  ) {}

  connect() {
    this.disconnect();
    if (!this.canTrack()) return;
    if (typeof document !== 'undefined') {
      document.addEventListener(
        'pointerdown',
        this.onDocumentPointerDown,
        true
      );
    }
    this.setupSubscriptions();
    this.check();
  }

  disconnect() {
    if (typeof document !== 'undefined') {
      document.removeEventListener(
        'pointerdown',
        this.onDocumentPointerDown,
        true
      );
    }
    this.clearPolling();
    this.clearPortalCloseTimer();
    this.clearReviewSavedTimer();
    this.destroyPortal();
    this.hoverIntent$.value = false;
    this.portalActive$.value = false;
    this.pointerInsideBadge = false;
    this.unsubscribeDoc?.();
    this.unsubscribeEnabled?.();
    this.unsubscribeProgress?.();
    this.unsubscribeDoc = undefined;
    this.unsubscribeEnabled = undefined;
    this.unsubscribeProgress = undefined;
  }

  refresh() {
    if (!this.canTrack()) return;
    this.indexed$.value = null;
    this.check();
  }

  render(): TemplateResult | null {
    if (!this.canRender()) {
      this.destroyPortal();
      return null;
    }

    const service = this.service;
    const vm = buildEmbeddingBadgeViewModel({
      indexed: this.indexed$.value,
      loading: this.loading$.value,
      failed: this.failed$.value,
      expanded: this.expanded$.value,
      enabled: this.enabled$.value,
      unavailable: this.unavailable$.value,
      imageAnalysis: this.reviewSummary$.value?.imageAnalysis ?? null,
      reindexCreditsBlocked: this.reindexCreditsBlocked$.value,
      progress: this.docProgress$.value ?? this.batchProgress$.value,
      service,
      onBadgeClick: this.onBadgeClick,
      onAction: this.onReindexClick,
      onDetails: this.options.getIndexReviewLocator
        ? this.onDetailsClick
        : undefined,
      onCheckAgain: this.onCheckAgain,
    });
    if (!vm) {
      this.destroyPortal();
      return null;
    }
    vm.hoverIntent = this.hoverIntent$.value;
    vm.isPointerPresent = this.isPointerPresent;
    vm.onHoverIntentChange = this.onHoverIntentChange;
    vm.onPointerPresenceChange = this.onPointerPresenceChange;
    if (this.reviewOpen$.value) {
      vm.reviewOpen = true;
      vm.reviewPanel = this.renderReviewPanel(service);
    }
    if (this.shouldRenderPortal(vm)) {
      this.renderPortal(vm);
      return null;
    }
    this.destroyPortal();
    return renderEmbeddingBadge(vm);
  }

  private get service(): IEmbeddingStatusService | undefined {
    return this.host.std.getOptional<IEmbeddingStatusService>(
      EmbeddingStatusProvider
    );
  }

  private canRender() {
    return this.options.canRender?.() ?? this.canTrack();
  }

  private canTrack() {
    return this.options.canTrack?.() ?? true;
  }

  private setupSubscriptions() {
    const service = this.service;
    if (!service) return;

    this.enabled$.value = service.isEmbeddingEnabled?.() ?? null;
    this.unsubscribeEnabled = service.onEnabledChange?.(enabled => {
      this.enabled$.value = enabled;
      this.host.requestUpdate();
    });

    const docId = this.host.store.id;
    this.unsubscribeDoc = service.onDocStatus(event => {
      if (event.docId !== docId) return;

      if (event.status === 'indexing') {
        this.loading$.value = true;
        this.failed$.value = false;
        this.docProgress$.value =
          event.chunksTotal && event.chunksTotal > 0
            ? { indexed: event.chunksIndexed ?? 0, total: event.chunksTotal }
            : null;
      } else if (event.status === 'indexed') {
        if (!this.userRequestedIndex) {
          this.loading$.value = false;
        }
        this.failed$.value = false;
        this.docProgress$.value = null;
        this.check();
      } else if (event.status === 'failed') {
        this.loading$.value = false;
        this.failed$.value = true;
        this.docProgress$.value = null;
        this.userRequestedIndex = false;
        this.check();
      }
      this.host.requestUpdate();
    });

    if (this.options.includeWorkspaceProgress) {
      this.syncWorkspaceProgress(service.getWorkspaceProgress());
      this.unsubscribeProgress = service.onWorkspaceProgress(event => {
        this.syncWorkspaceProgress(event);
        this.host.requestUpdate();
      });
    }
  }

  private syncWorkspaceProgress(
    progress: ReturnType<IEmbeddingStatusService['getWorkspaceProgress']>
  ) {
    if (!progress || progress.docsIndexed >= progress.docsTotal) {
      this.batchProgress$.value = null;
      return;
    }
    this.batchProgress$.value = {
      indexed: progress.docsIndexed,
      total: progress.docsTotal,
    };
  }

  private check() {
    if (!this.canTrack()) return;
    const service = this.service;
    if (!service) {
      this.unavailable$.value = true;
      this.host.requestUpdate();
      return;
    }

    const docId = this.host.store.id;
    service
      .hasEmbeddings(docId, this.host.model.id)
      .then(indexed => {
        this.indexed$.value = indexed;
        this.unavailable$.value = false;
        this.failed$.value = false;
        if (!this.userRequestedIndex) {
          this.loading$.value = false;
        }
        this.docProgress$.value = null;
        // Only indexed content has a review; a block just added may not even
        // be stored on the server yet.
        if (indexed)
          this.ensureReviewSummaryLoaded({ force: this.userRequestedIndex });
        this.completeUserRequestedIndexIfReady(service);
        this.host.requestUpdate();
      })
      .catch(error => {
        this.indexed$.value = null;
        this.loading$.value = false;
        this.unavailable$.value = isEmbeddingStatusUnavailableError(error);
        this.failed$.value = !this.unavailable$.value;
        this.host.requestUpdate();
      });
  }

  private readonly onBadgeClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (this.loading$.value) return;
    this.expanded$.value = !this.expanded$.value;
    this.host.requestUpdate();
  };

  private readonly onHoverIntentChange = (active: boolean) => {
    const shouldCollapse =
      !active && this.expanded$.value && !this.reviewOpen$.value;
    if (this.hoverIntent$.value === active && !shouldCollapse) return;
    this.hoverIntent$.value = active;
    if (shouldCollapse) {
      this.expanded$.value = false;
    }
    this.host.requestUpdate();
  };

  private readonly onPointerPresenceChange = (active: boolean) => {
    this.pointerInsideBadge = active;
    this.clearPortalCloseTimer();
    if (active) {
      if (this.portalActive$.value) return;
      this.portalActive$.value = true;
      this.host.requestUpdate();
      return;
    }
    this.portalCloseTimer = setTimeout(() => {
      this.portalCloseTimer = undefined;
      if (!this.portalActive$.value) return;
      this.portalActive$.value = false;
      this.host.requestUpdate();
    }, PORTAL_CLOSE_DELAY_MS);
  };

  private readonly isPointerPresent = () => this.pointerInsideBadge;

  private clearPortalCloseTimer() {
    if (this.portalCloseTimer === undefined) return;
    clearTimeout(this.portalCloseTimer);
    this.portalCloseTimer = undefined;
  }

  private readonly onDocumentPointerDown = (event: PointerEvent) => {
    if (
      !this.reviewOpen$.value &&
      !this.expanded$.value &&
      !this.hoverIntent$.value
    ) {
      return;
    }
    const eventPath = event.composedPath();
    const badgeElement = this.hostElement()?.querySelector(
      '.affine-embedding-badge'
    );
    if (
      (badgeElement && eventPath.includes(badgeElement)) ||
      (this.portalElement && eventPath.includes(this.portalElement))
    ) {
      return;
    }
    this.reviewOpen$.value = false;
    this.reviewRegenerationOpenField$.value = null;
    this.expanded$.value = false;
    this.hoverIntent$.value = false;
    this.host.requestUpdate();
  };

  private shouldRenderPortal(vm: EmbeddingBadgeViewModel): boolean {
    return (
      vm.reviewOpen === true || vm.expanded === true || this.portalActive$.value
    );
  }

  private renderPortal(vm: EmbeddingBadgeViewModel) {
    if (typeof document === 'undefined') return;
    ensureEmbeddingBadgePortalStyles();
    this.portalUsesCanvasZoom = vm.reviewOpen !== true;
    const portal = this.ensurePortalElement();
    renderLit(renderEmbeddingBadge(vm), portal);
    requestAnimationFrame(() => {
      if (this.portalElement === portal && portal.matches(':hover')) {
        this.onPointerPresenceChange(true);
      }
    });
    this.ensurePortalTracking();
    this.syncPortalPosition();
  }

  private ensurePortalElement(): HTMLDivElement {
    if (this.portalElement?.isConnected) return this.portalElement;
    const portal = document.createElement('div');
    portal.className = 'affine-embedding-badge-portal';
    document.body.append(portal);
    this.portalElement = portal;
    return portal;
  }

  private ensurePortalTracking() {
    if (this.portalTrackingActive || typeof window === 'undefined') return;
    this.portalTrackingActive = true;
    window.addEventListener('resize', this.schedulePortalPositionSync);
    window.addEventListener('scroll', this.schedulePortalPositionSync, true);
    const anchor = this.portalAnchorElement();
    if (typeof ResizeObserver !== 'undefined' && anchor && this.portalElement) {
      this.portalResizeObserver = new ResizeObserver(
        this.schedulePortalPositionSync
      );
      this.portalResizeObserver.observe(anchor);
      this.portalResizeObserver.observe(this.portalElement);
    }
    this.portalViewportSubscription =
      this.hostElement()?.gfx?.viewport?.viewportUpdated?.subscribe?.(
        this.schedulePortalPositionSync
      );
  }

  private disposePortalTracking() {
    if (!this.portalTrackingActive || typeof window === 'undefined') return;
    this.portalTrackingActive = false;
    window.removeEventListener('resize', this.schedulePortalPositionSync);
    window.removeEventListener('scroll', this.schedulePortalPositionSync, true);
    this.portalResizeObserver?.disconnect();
    this.portalResizeObserver = null;
    disposeLike(this.portalViewportSubscription);
    this.portalViewportSubscription = null;
  }

  private destroyPortal() {
    if (
      this.portalPositionRaf !== null &&
      typeof cancelAnimationFrame !== 'undefined'
    ) {
      cancelAnimationFrame(this.portalPositionRaf);
      this.portalPositionRaf = null;
    }
    this.disposePortalTracking();
    if (!this.portalElement) return;
    renderLit(nothing, this.portalElement);
    this.portalElement.remove();
    this.portalElement = null;
  }

  private readonly schedulePortalPositionSync = () => {
    if (
      this.portalPositionRaf !== null ||
      typeof requestAnimationFrame === 'undefined'
    ) {
      if (typeof requestAnimationFrame === 'undefined') {
        this.syncPortalPosition();
      }
      return;
    }
    this.portalPositionRaf = requestAnimationFrame(() => {
      this.portalPositionRaf = null;
      this.syncPortalPosition();
    });
  };

  private syncPortalPosition() {
    const portal = this.portalElement;
    const anchor = this.portalAnchorElement();
    if (!portal || !anchor || typeof window === 'undefined') return;
    const margin = 8;
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const scale = this.portalUsesCanvasZoom ? this.canvasZoom() : 1;
    portal.style.transform = scale === 1 ? '' : `scale(${scale})`;
    portal.style.setProperty(
      '--affine-embedding-badge-inline-max-width',
      `${Math.max(32, (viewportWidth - margin * 2) / scale)}px`
    );
    const anchorRect = anchor.getBoundingClientRect();
    const portalRect = portal.getBoundingClientRect();
    const width = portalRect.width || 32 * scale;
    const height = portalRect.height || 32 * scale;
    const maxLeft = Math.max(margin, viewportWidth - width - margin);
    const maxTop = Math.max(margin, viewportHeight - height - margin);
    const anchorInset = margin * scale;
    const preferredLeft = anchorRect.right - width - anchorInset;
    const preferredTop = anchorRect.bottom - height - anchorInset;
    const left = clamp(preferredLeft, margin, maxLeft);
    const top = clamp(preferredTop, margin, maxTop);
    portal.style.left = `${Math.round(left)}px`;
    portal.style.top = `${Math.round(top)}px`;
  }

  private canvasZoom(): number {
    const zoom = this.hostElement()?.gfx?.viewport?.zoom;
    return typeof zoom === 'number' && Number.isFinite(zoom) && zoom > 0
      ? zoom
      : 1;
  }

  private hostElement(): ViewportAwareHost | null {
    return typeof HTMLElement !== 'undefined' &&
      this.host instanceof HTMLElement
      ? (this.host as ViewportAwareHost)
      : null;
  }

  private portalAnchorElement(): ViewportAwareHost | null {
    return (
      (this.options.getPortalAnchor?.() as ViewportAwareHost | null) ??
      this.hostElement()
    );
  }

  private readonly onReindexClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (this.loading$.value) return;

    const service = this.service;
    if (!service) return;

    this.expanded$.value = false;
    this.loading$.value = true;
    this.failed$.value = false;
    this.unavailable$.value = false;
    this.reindexCreditsBlocked$.value = false;
    this.userRequestedIndex = true;
    this.host.requestUpdate();

    const locator = this.options.getIndexReviewLocator?.() ?? null;
    service
      .reindex(this.host.store.id, locator)
      .then(() => {
        this.indexed$.value = null;
        this.reviewSummary$.value = null;
        this.reviewSummaryLocatorKey = null;
        this.notify(service, 'indexingStarted');
        this.startAggressivePolling();
      })
      .catch(error => {
        this.loading$.value = false;
        if (isEmbeddingCreditsRequiredError(error)) {
          this.reindexCreditsBlocked$.value = true;
          this.expanded$.value = true;
        } else {
          this.notify(service, 'indexingFailed');
          this.unavailable$.value = isEmbeddingStatusUnavailableError(error);
          this.failed$.value = !this.unavailable$.value;
        }
        this.userRequestedIndex = false;
        this.host.requestUpdate();
      });
  };

  private readonly onCheckAgain = (event: MouseEvent) => {
    event.stopPropagation();
    this.unavailable$.value = false;
    this.indexed$.value = null;
    this.loading$.value = true;
    this.failed$.value = false;
    this.check();
    this.host.requestUpdate();
  };

  private readonly onDetailsClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.expanded$.value = false;
    this.reviewOpen$.value = true;
    this.loadReview({ force: true });
    this.host.requestUpdate();
  };

  private readonly onReviewClose = (event: MouseEvent) => {
    event.stopPropagation();
    this.reviewOpen$.value = false;
    this.reviewRegenerationOpenField$.value = null;
    this.host.requestUpdate();
  };

  private ensureReviewSummaryLoaded(options?: { force?: boolean }) {
    if (this.reviewOpen$.value) return;
    this.loadReview({ force: options?.force, silent: true });
  }

  private loadReview(options?: { force?: boolean; silent?: boolean }) {
    const service = this.service;
    const locator = this.options.getIndexReviewLocator?.();
    if (!service?.getIndexReviewSubject || !locator) {
      if (!options?.silent) {
        this.reviewError$.value = this.label(service, 'reviewLoadFailed');
        this.reviewLoading$.value = false;
      }
      return;
    }
    const locatorKey = JSON.stringify(locator);
    if (this.reviewSummaryLoading) {
      return;
    }
    if (
      !options?.force &&
      this.reviewSummaryLocatorKey === locatorKey &&
      this.reviewSummary$.value
    ) {
      return;
    }

    this.reviewSummaryLoading = true;
    this.reviewSummaryLocatorKey = locatorKey;
    if (!options?.silent) {
      this.reviewLoading$.value = true;
      this.reviewError$.value = null;
    }
    service
      .getIndexReviewSubject(locator)
      .then(summary => {
        this.reviewError$.value = null;
        this.reviewSummary$.value = summary;
        this.resetReviewDrafts(summary);
        this.completeUserRequestedIndexIfReady(service);
        if (!options?.silent) {
          this.reviewLoading$.value = false;
        }
        this.host.requestUpdate();
      })
      .catch(() => {
        this.reviewSummaryLocatorKey = null;
        if (!options?.silent) {
          this.reviewError$.value = this.label(service, 'reviewLoadFailed');
          this.reviewLoading$.value = false;
          this.host.requestUpdate();
        }
      })
      .finally(() => {
        this.reviewSummaryLoading = false;
      });
  }

  private renderReviewPanel(
    service: Pick<IEmbeddingStatusService, 'getLabel'> | null | undefined
  ): TemplateResult {
    const summary = this.reviewSummary$.value;
    const loading = this.reviewLoading$.value;
    const error = this.reviewError$.value;
    const notice = this.reviewNotice$.value;
    const sections = [
      'visualDescription',
      'searchTerms',
      'ocrText',
    ] satisfies EmbeddingIndexReviewField[];

    return html`
      <div
        class="affine-embedding-badge-review-panel"
        @click=${stopReviewEvent}
      >
        <div class="affine-embedding-badge-review-header">
          <div class="affine-embedding-badge-review-title">
            ${this.label(service, 'reviewPanelTitle')}
          </div>
          <button
            type="button"
            class="affine-embedding-badge-review-close"
            @click=${this.onReviewClose}
          >
            ${this.label(service, 'reviewPanelClose')}
          </button>
        </div>
        ${loading && !summary
          ? html`<div class="affine-embedding-badge-review-muted">
              ${this.label(service, 'indexStatusIndexing')}
            </div>`
          : nothing}
        ${error
          ? html`<div class="affine-embedding-badge-review-muted">
              ${error}
            </div>`
          : nothing}
        ${notice
          ? html`<div class="affine-embedding-badge-review-muted">
              ${notice}
            </div>`
          : nothing}
        ${summary
          ? sections.map(field => this.renderReviewSection(service, field))
          : nothing}
      </div>
    `;
  }

  private renderReviewSection(
    service: Pick<IEmbeddingStatusService, 'getLabel'> | null | undefined,
    field: EmbeddingIndexReviewField
  ): TemplateResult {
    const summary = this.reviewSummary$.value;
    const canWrite = summary?.canWrite === true;
    const draft = this.reviewDrafts.get(field) ?? '';
    const hint =
      this.reviewHints.get(field as EmbeddingIndexReviewRegenerableField) ?? '';
    const busy = this.reviewBusyField$.value;
    const editable = canWrite;
    const regenerable = canWrite && field !== 'manualSearchAdditions';
    const regenerationOpen =
      regenerable && this.reviewRegenerationOpenField$.value === field;
    const currentValue = summary ? this.fieldDraftValue(summary, field) : '';
    const dirty = draft !== currentValue;
    const saved = this.reviewSavedField$.value === field;

    const hasEditor = true;

    return html`
      <section
        class=${classMap({
          'affine-embedding-badge-review-section': true,
          'has-editor': hasEditor,
        })}
      >
        <div class="affine-embedding-badge-review-section-header">
          <div class="affine-embedding-badge-review-section-title">
            ${this.reviewFieldLabel(service, field)}
          </div>
          ${canWrite
            ? nothing
            : html`<span class="affine-embedding-badge-review-muted">
                ${this.label(service, 'reviewReadOnly')}
              </span>`}
        </div>
        <div class="affine-embedding-badge-review-content">
          ${hasEditor
            ? html`
                <div class="affine-embedding-badge-review-editor">
                  <textarea
                    class="affine-embedding-badge-review-textarea"
                    ${ref(autoSizeReviewTextarea)}
                    .value=${draft}
                    ?readonly=${!editable}
                    @input=${(event: InputEvent) =>
                      this.setReviewDraft(field, event)}
                  ></textarea>
                  ${editable
                    ? html`
                        <div
                          class="affine-embedding-badge-review-actions affine-embedding-badge-review-main-actions"
                        >
                          ${regenerable
                            ? html`
                                <button
                                  type="button"
                                  class="affine-embedding-badge-review-button ghost"
                                  aria-expanded=${regenerationOpen
                                    ? 'true'
                                    : 'false'}
                                  ?disabled=${busy !== null}
                                  @click=${() =>
                                    this.openReviewRegeneration(
                                      field as EmbeddingIndexReviewRegenerableField
                                    )}
                                >
                                  <span aria-hidden="true">✦</span>
                                  ${this.label(
                                    service,
                                    'reviewRegenerateWithHint'
                                  )}
                                </button>
                              `
                            : html`<span></span>`}
                          <button
                            type="button"
                            class=${classMap({
                              'affine-embedding-badge-review-button': true,
                              primary: !saved,
                              saved,
                            })}
                            ?disabled=${busy !== null || !dirty || saved}
                            @click=${() => this.saveReviewField(field)}
                          >
                            ${busy === `save:${field}`
                              ? this.label(service, 'reviewSaving')
                              : saved
                                ? `✓ ${this.label(service, 'reviewSaved')}`
                                : this.label(service, 'reviewSave')}
                          </button>
                        </div>
                      `
                    : nothing}
                  ${regenerationOpen
                    ? html`
                        <div class="affine-embedding-badge-review-regenerate">
                          <div
                            class="affine-embedding-badge-review-regenerate-body"
                          >
                            <textarea
                              class="affine-embedding-badge-review-textarea affine-embedding-badge-review-hint"
                              ${ref(autoSizeReviewTextarea)}
                              placeholder=${this.label(
                                service,
                                'reviewHintPlaceholder'
                              )}
                              .value=${hint}
                              @input=${(event: InputEvent) =>
                                this.setReviewHint(
                                  field as EmbeddingIndexReviewRegenerableField,
                                  event
                                )}
                            ></textarea>
                            <div class="affine-embedding-badge-review-muted">
                              ${this.label(service, 'reviewReplaceWarning')}
                            </div>
                            <div
                              class="affine-embedding-badge-review-actions affine-embedding-badge-review-regenerate-actions"
                            >
                              <button
                                type="button"
                                class="affine-embedding-badge-review-button"
                                ?disabled=${busy !== null}
                                @click=${this.closeReviewRegeneration}
                              >
                                ${this.label(service, 'reviewCancel')}
                              </button>
                              <button
                                type="button"
                                class="affine-embedding-badge-review-button primary"
                                ?disabled=${busy !== null || !hint.trim()}
                                @click=${() =>
                                  this.regenerateReviewField(
                                    field as EmbeddingIndexReviewRegenerableField
                                  )}
                              >
                                ${busy === `regen:${field}`
                                  ? this.label(service, 'reviewRegenerating')
                                  : this.label(
                                      service,
                                      'reviewRegenerateAction'
                                    )}
                              </button>
                            </div>
                          </div>
                        </div>
                      `
                    : nothing}
                </div>
              `
            : nothing}
        </div>
      </section>
    `;
  }

  private resetReviewDrafts(summary: EmbeddingIndexReviewSubjectSummary) {
    for (const field of [
      'visualDescription',
      'searchTerms',
      'ocrText',
    ] satisfies EmbeddingIndexReviewField[]) {
      this.reviewDrafts.set(field, this.fieldDraftValue(summary, field));
    }
  }

  private fieldDraftValue(
    summary: EmbeddingIndexReviewSubjectSummary,
    field: EmbeddingIndexReviewField
  ): string {
    const current = activeVersion(summary, field);
    return current ? this.versionValueToText(field, current) : '';
  }

  private activeVersion(
    field: EmbeddingIndexReviewField
  ): EmbeddingIndexReviewCurrentValue | null {
    const summary = this.reviewSummary$.value;
    return summary ? activeVersion(summary, field) : null;
  }

  private versionValueToText(
    field: EmbeddingIndexReviewField,
    version: EmbeddingIndexReviewCurrentValue
  ): string {
    if (field === 'searchTerms') return formatSearchTerms(version.value);
    if (field === 'manualSearchAdditions')
      return formatStringList(version.value);
    return typeof version.value === 'string' ? version.value : '';
  }

  private setReviewDraft(field: EmbeddingIndexReviewField, event: InputEvent) {
    const target = event.currentTarget;
    if (target instanceof HTMLTextAreaElement) {
      autoSizeReviewTextarea(target);
      this.reviewDrafts.set(field, target.value);
      if (this.reviewSavedField$.value === field) {
        this.clearReviewSavedTimer();
        this.reviewSavedField$.value = null;
      }
      this.host.requestUpdate();
    }
  }

  private setReviewHint(
    field: EmbeddingIndexReviewRegenerableField,
    event: InputEvent
  ) {
    const target = event.currentTarget;
    if (target instanceof HTMLTextAreaElement) {
      autoSizeReviewTextarea(target);
      this.reviewHints.set(field, target.value);
      this.host.requestUpdate();
    }
  }

  private openReviewRegeneration(field: EmbeddingIndexReviewRegenerableField) {
    this.reviewRegenerationOpenField$.value = field;
    this.host.requestUpdate();
  }

  private readonly closeReviewRegeneration = () => {
    this.reviewRegenerationOpenField$.value = null;
    this.host.requestUpdate();
  };

  private saveReviewField(field: EmbeddingIndexReviewField) {
    const service = this.service;
    const locator = this.options.getIndexReviewLocator?.();
    if (!service?.saveIndexReviewField || !locator) return;
    this.reviewBusyField$.value = `save:${field}`;
    this.reviewNotice$.value = null;
    const value = reviewDraftValue(field, this.reviewDrafts.get(field) ?? '');
    const expectedActiveVersionId = this.activeVersion(field)?.id ?? null;
    service
      .saveIndexReviewField(locator, field, value, {
        expectedActiveVersionId,
      })
      .then(() => {
        this.showReviewSaved(field);
        this.loadReview({ force: true });
      })
      .catch(error => {
        this.handleReviewMutationError(service, error);
      })
      .finally(() => {
        this.reviewBusyField$.value = null;
        this.host.requestUpdate();
      });
    this.host.requestUpdate();
  }

  private showReviewSaved(field: EmbeddingIndexReviewField) {
    this.clearReviewSavedTimer();
    this.reviewSavedField$.value = field;
    this.reviewSavedTimer = setTimeout(() => {
      this.reviewSavedTimer = undefined;
      if (this.reviewSavedField$.value === field) {
        this.reviewSavedField$.value = null;
        this.host.requestUpdate();
      }
    }, 1600);
  }

  private clearReviewSavedTimer() {
    if (this.reviewSavedTimer) clearTimeout(this.reviewSavedTimer);
    this.reviewSavedTimer = undefined;
  }

  private regenerateReviewField(field: EmbeddingIndexReviewRegenerableField) {
    const service = this.service;
    const locator = this.options.getIndexReviewLocator?.();
    const userHint = this.reviewHints.get(field)?.trim() ?? '';
    if (!service?.regenerateIndexReviewField || !locator || !userHint) return;

    this.reviewBusyField$.value = `regen:${field}`;
    this.reviewNotice$.value = null;
    const active = this.activeVersion(field);
    service
      .regenerateIndexReviewField(locator, field, userHint, {
        confirmed: Boolean(active),
        expectedActiveVersionId: active?.id ?? null,
        mediaType: this.options.mediaType?.() ?? null,
      })
      .then(result => {
        if (!result.queued) {
          this.reviewError$.value = this.label(
            service,
            'indexStatusUnavailable'
          );
          return;
        }
        this.reviewHints.set(field, '');
        this.reviewRegenerationOpenField$.value = null;
        this.reviewNotice$.value = this.label(service, 'reviewQueued');
      })
      .catch(error => {
        this.handleReviewMutationError(service, error);
      })
      .finally(() => {
        this.reviewBusyField$.value = null;
        this.host.requestUpdate();
      });
    this.host.requestUpdate();
  }

  private handleReviewMutationError(
    service: IEmbeddingStatusService,
    error: unknown
  ) {
    if (isIndexReviewConflictError(error)) {
      this.reviewError$.value = this.label(service, 'reviewConflict');
      this.loadReview({ force: true });
      return;
    }
    this.reviewError$.value = this.label(service, 'reviewLoadFailed');
  }

  private reviewFieldLabel(
    service: Pick<IEmbeddingStatusService, 'getLabel'> | null | undefined,
    field: EmbeddingIndexReviewField
  ): string {
    switch (field) {
      case 'visualDescription':
        return this.label(service, 'reviewSectionDescription');
      case 'searchTerms':
        return this.label(service, 'reviewSectionSearchTerms');
      case 'ocrText':
      case 'pageText':
        return this.label(service, 'reviewSectionExtractedText');
      case 'manualSearchAdditions':
        return this.label(service, 'reviewSectionManualAdditions');
    }
  }

  private label(
    service: Pick<IEmbeddingStatusService, 'getLabel'> | null | undefined,
    key: Parameters<typeof getEmbeddingStatusLabel>[1]
  ): string {
    return getEmbeddingStatusLabel(service, key);
  }

  private notify(
    service: IEmbeddingStatusService,
    key: Parameters<typeof getEmbeddingStatusLabel>[1]
  ) {
    this.options.notify?.(getEmbeddingStatusLabel(service, key));
  }

  private startAggressivePolling() {
    this.clearPolling();
    let elapsed = 0;
    this.checkTimer = setInterval(() => {
      elapsed += 3000;
      this.check();
      if (elapsed >= 60_000) {
        this.userRequestedIndex = false;
        this.loading$.value = false;
        this.failed$.value = true;
        this.host.requestUpdate();
        this.startIdlePolling();
      } else if (!this.userRequestedIndex) {
        this.startIdlePolling();
      }
    }, 3_000);
  }

  private completeUserRequestedIndexIfReady(
    service: IEmbeddingStatusService
  ): boolean {
    if (!this.userRequestedIndex || this.indexed$.value !== true) return false;
    const locator = this.options.getIndexReviewLocator?.() ?? null;
    if (
      locator?.subjectKind === 'image-block' &&
      this.reviewSummary$.value?.imageAnalysis?.state !== 'complete'
    ) {
      return false;
    }
    this.loading$.value = false;
    this.failed$.value = false;
    this.notify(service, 'indexedSuccessfully');
    this.userRequestedIndex = false;
    return true;
  }

  private startIdlePolling() {
    const idlePollMs = this.options.idlePollMs;
    this.clearPolling();
    if (!idlePollMs) return;
    this.checkTimer = setInterval(() => this.check(), idlePollMs);
  }

  private clearPolling() {
    if (!this.checkTimer) return;
    clearInterval(this.checkTimer);
    this.checkTimer = undefined;
  }
}

function stopReviewEvent(event: Event) {
  event.stopPropagation();
}

function autoSizeReviewTextarea(element: Element | undefined) {
  if (!(element instanceof HTMLTextAreaElement)) return;
  element.style.height = 'auto';
  const maxHeight = Number.parseFloat(getComputedStyle(element).maxHeight);
  const nextHeight = Number.isFinite(maxHeight)
    ? Math.min(element.scrollHeight, maxHeight)
    : element.scrollHeight;
  element.style.height = `${nextHeight}px`;
  element.style.overflowY =
    Number.isFinite(maxHeight) && element.scrollHeight > maxHeight
      ? 'auto'
      : 'hidden';
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function disposeLike(disposable: DisposableLike) {
  if (!disposable) return;
  if (typeof disposable === 'function') {
    disposable();
    return;
  }
  disposable.dispose?.();
  disposable.unsubscribe?.();
}

function isIndexReviewConflictError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    Number(error.status) === 409
  );
}

function activeVersion(
  summary: EmbeddingIndexReviewSubjectSummary,
  field: EmbeddingIndexReviewField
): EmbeddingIndexReviewCurrentValue | null {
  return summary.currentValues.find(version => version.field === field) ?? null;
}

function formatSearchTerms(value: unknown): string {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  return Object.entries(value as Record<string, unknown>)
    .flatMap(([locale, terms]) => {
      if (!Array.isArray(terms)) return [];
      const normalizedTerms = terms.filter(
        (term): term is string => typeof term === 'string' && term.trim() !== ''
      );
      if (!normalizedTerms.length) return [];
      return `${locale}: ${normalizedTerms.join(', ')}`;
    })
    .join('\n');
}

function formatStringList(value: unknown): string {
  if (!Array.isArray(value)) return '';
  return value
    .filter((entry): entry is string => typeof entry === 'string')
    .join('\n');
}

function reviewDraftValue(
  field: EmbeddingIndexReviewField,
  draft: string
): unknown {
  if (field === 'searchTerms') return parseSearchTermsDraft(draft);
  if (field === 'manualSearchAdditions') return parseListDraft(draft);
  return draft.trim();
}

function parseSearchTermsDraft(draft: string): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  for (const rawLine of draft.split(/\n/u)) {
    const line = rawLine.trim();
    if (!line) continue;
    const separatorIndex = line.indexOf(':');
    const locale =
      separatorIndex > 0 ? line.slice(0, separatorIndex).trim() : 'und';
    const normalizedLocale = locale.toLocaleLowerCase();
    const rawTerms = separatorIndex > 0 ? line.slice(separatorIndex + 1) : line;
    const terms = rawTerms
      .split(',')
      .map(term => term.trim())
      .filter(Boolean);
    if (terms.length) {
      result[normalizedLocale] = Array.from(
        new Set([...(result[normalizedLocale] ?? []), ...terms])
      );
    }
  }
  return result;
}

function parseListDraft(draft: string): string[] {
  return Array.from(
    new Set(
      draft
        .split(/\n/u)
        .map(line => line.trim())
        .filter(Boolean)
    )
  );
}
